/**
 * Appwrite Authentication Service Adapter
 *
 * Implements IAuthService interface using Appwrite Account API
 */

import {
  Client,
  Account,
  Models,
  OAuthProvider,
  Teams,
  ID,
  Query,
  Storage,
} from "appwrite";
import { IAuthService, IUser } from "../interfaces/IAuthService";
import { IStorageService } from "../interfaces/IStorageService";
import { n8nService } from "../../../components/core/services/n8n.service";
import { normalizeLocale } from "../../../constants/locales";
import { buildDefaultTeamPrefs } from "../../defaultTeamPrefs";
import { mergePrefs, decodeStoragePrefs, decodePrefValue } from "../../appwritePrefsSchema";
import { geoService } from "../../geo.service";
import { createAsyncRequestCache } from "../../../utils/asyncRequestCache";

export class AppwriteAuthService implements IAuthService {
  private client: Client;
  private account: Account;
  private teams: Teams;
  private storage: IStorageService;
  private currentUserCache: IUser | null = null;
  private authStateListeners: Array<(user: IUser | null) => void> = [];
  private initializationPromise: Promise<void>;
  private targetEmail: string | null = null;
  private readonly TARGET_EMAIL_STORAGE_KEY = 'brainiacs_impersonated_email';

  constructor(client: Client, storage: IStorageService) {
    this.client = client;
    this.account = new Account(client);
    this.teams = new Teams(client);
    this.storage = storage;

    // Load target email from session storage if present
    const savedTarget = sessionStorage.getItem(this.TARGET_EMAIL_STORAGE_KEY);
    if (savedTarget) {
      this.targetEmail = savedTarget;
      console.log('👤 [IMPERSONATION] Restored impersonated email:', savedTarget);
    }

    // Initialize current user and store the promise
    this.initializationPromise = this.initializeUser();
  }

  private getBaseAppUrl(): string {
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    const productionUrl =
      import.meta.env.VITE_APP_URL ||
      "https://brainiacs--os-u12020.vm.elestio.app";

    return isLocalhost ? window.location.origin : productionUrl;
  }

  private getMagicLinkCallbackUrl(): string {
    return `${this.getBaseAppUrl()}/auth/callback?type=magic-link`;
  }

  /**
   * Initialize current user on service creation
   */
  private async initializeUser(): Promise<void> {
    try {
      const session = await this.account.get();
      this.currentUserCache = this.toIUser(session);
      this.notifyAuthStateListeners(this.currentUserCache);
      console.log(
        "✅ Appwrite session detected:",
        this.currentUserCache?.email,
        "| labels:", this.currentUserCache?.labels,
      );
    } catch (error: any) {
      // No active session (401 is expected when not logged in)
      this.currentUserCache = null;
      this.notifyAuthStateListeners(null);
      // Only log if it's not a 401 (401 is expected when no session exists)
      if (error.code !== 401) {
        console.warn("⚠️ Unexpected error checking Appwrite session:", error);
      }
    }
  }

  /**
   * Convert Appwrite User to IUser
   */
  private toIUser(user: Models.User<Models.Preferences> | null): IUser | null {
    if (!user) return null;

    // Get profile picture from user preferences (decode storage "null")
    const photoURL = decodePrefValue(user.prefs?.profilePictureUrl) || null;
    const status = decodePrefValue((user.prefs as Record<string, any> | undefined)?.status) || null;

    return {
      uid: user.$id,
      email: user.email || null,
      displayName: user.name || null,
      photoURL: photoURL,
      labels: user.labels ?? [],
      status: typeof status === 'string' ? status : null,
    };
  }

  /**
   * Re-check the Appwrite session and notify listeners with the result.
   *
   * The SDK's auth listener only fires on init/sign-in/sign-out — it emits NO
   * event when connectivity returns after a dropout, so a user cleared by the
   * dropout would stare at the login screen forever. The AuthContext calls
   * this on `window online` and on a short poll WHILE a dropout-clear is in
   * effect; a resolved session re-fires the listener and the app self-heals
   * without a manual refresh. A 401 here is the definitive "signed out" signal.
   */
  async revalidateSession(): Promise<IUser | null> {
    try {
      const session = await this.account.get();
      this.currentUserCache = this.toIUser(session);
      this.notifyAuthStateListeners(this.currentUserCache);
      return this.currentUserCache;
    } catch (error: any) {
      if (error?.code === 401) {
        // Definitive sign-out (session revoked server-side) — propagate it.
        this.currentUserCache = null;
        this.notifyAuthStateListeners(null);
        return null;
      }
      // Still unreachable — leave cache untouched; the caller keeps polling.
      console.warn("⚠️ Session revalidation unreachable, will retry:", error?.message);
      return this.currentUserCache;
    }
  }

  /**
   * Notify all auth state listeners
   */
  private notifyAuthStateListeners(user: IUser | null): void {
    this.authStateListeners.forEach((listener) => {
      try {
        listener(user);
      } catch (error) {
        console.error("❌ Error in auth state listener:", error);
      }
    });
  }

  /**
   * Download profile picture from URL and upload to Appwrite Storage
   * Returns the Appwrite Storage URL
   */
  private async uploadProfilePictureFromUrl(
    pictureUrl: string,
    userId: string,
  ): Promise<string | null> {
    try {
      console.log("📸 Downloading profile picture from OAuth provider...");

      // Fetch the image
      const response = await fetch(pictureUrl);
      if (!response.ok) {
        throw new Error(`Failed to download image: ${response.statusText}`);
      }

      // Convert to blob
      const blob = await response.blob();

      // Upload via the file path (reuses the common upload logic)
      return this.uploadProfilePictureFromBlob(blob, userId);
    } catch (error: any) {
      console.error(
        "⚠️ Failed to upload profile picture (non-critical):",
        error.message,
      );
      // Return null on error - profile picture is optional
      return null;
    }
  }

  /**
   * Upload a File/Blob (e.g. from client-side crop dialog) as the user's
   * profile picture. Appends a cache-busting timestamp so the browser
   * re-fetches on re-upload (deterministic file ID → same URL otherwise).
   */
  private async uploadProfilePictureFromFile(
    blob: Blob | File,
    userId: string,
  ): Promise<string | null> {
    try {
      return this.uploadProfilePictureFromBlob(blob, userId);
    } catch (error: any) {
      console.error(
        "⚠️ Failed to upload cropped profile picture (non-critical):",
        error.message,
      );
      return null;
    }
  }

  /** Shared blob→storage logic with cache-bust. */
  private async uploadProfilePictureFromBlob(
    blob: Blob,
    userId: string,
  ): Promise<string | null> {
    const filePath = `profile-pictures/${userId}`;
    const storageUrl = await this.storage.uploadFile(filePath, blob);

    // Cache-bust: deterministic file ID means re-upload returns the SAME URL.
    // The browser would show the old image unless we bust its cache.
    const bustedUrl = storageUrl.includes('?')
      ? `${storageUrl}&cb=${Date.now()}`
      : `${storageUrl}?cb=${Date.now()}`;

    console.log("✅ Profile picture uploaded + cache-busted:", bustedUrl);
    return bustedUrl;
  }

  /**
   * Handle OAuth profile picture after successful authentication
   * Downloads profile picture from OAuth provider and uploads to Appwrite Storage
   */
  private async handleOAuthProfilePicture(
    user: Models.User<Models.Preferences>,
    provider: "google" | "linkedin",
  ): Promise<void> {
    try {
      // Check if user already has a profile picture stored in preferences
      const existingPhotoUrl = decodePrefValue(
        user.prefs?.profilePictureUrl,
      ) as string | null;
      if (existingPhotoUrl) {
        console.log("⏭️ Skipping profile picture upload (already exists)");
        return;
      }

      console.log(`📸 Fetching ${provider} OAuth profile data...`);

      // Get OAuth identities to access profile data
      const identities = await this.account.listIdentities();
      console.log(
        `🔍 Found ${identities.identities.length} identities for user`,
      );

      // Find the identity for the current provider
      const identity = identities.identities.find(
        (id: any) => id.provider === provider,
      );

      if (!identity) {
        console.log(`⚠️ No ${provider} identity found`);
        return;
      }

      console.log(`✅ Found ${provider} identity`);

      // Ensure user name is set from OAuth provider if not already set
      if (!user.name && identity.providerEmail) {
        // Extract name from email as fallback
        const nameFromEmail = identity.providerEmail
          .split("@")[0]
          .split(".")
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        console.log(`📝 Setting user name from email: ${nameFromEmail}`);

        try {
          await this.account.updateName(nameFromEmail);
          user.name = nameFromEmail;

          // Update cached user immediately with new name
          if (this.currentUserCache) {
            this.currentUserCache = {
              ...this.currentUserCache,
              displayName: nameFromEmail,
            };
            this.notifyAuthStateListeners(this.currentUserCache);
          }

          console.log(`✅ User name updated to: ${nameFromEmail}`);
        } catch (nameError: any) {
          console.warn(
            "⚠️ Failed to update user name (non-critical):",
            nameError.message,
          );
        }
      }

      // Extract profile picture URL based on provider
      let pictureUrl: string | null = null;

      if (provider === "google") {
        // For Google OAuth, try multiple approaches to get profile picture:
        // 1. Check if Appwrite stored it in identity data (from OAuth response)
        // 2. Use UI Avatars as fallback (generates avatar from name/email)

        // Check if identity has profile picture data from OAuth
        // Appwrite may store this in accessTokenData or providerAccessToken fields
        const identityData = identity as any;
        if (identityData.providerAccessTokenData?.picture) {
          pictureUrl = identityData.providerAccessTokenData.picture;
          console.log(`✅ Found Google profile picture in identity data`);
        } else if (identityData.picture) {
          pictureUrl = identityData.picture;
          console.log(`✅ Found Google profile picture in identity data`);
        }

        // Note: Google's googleusercontent URLs require additional OAuth scopes
        // (userinfo.profile) which are not included by default.
        // Using UI Avatars fallback is more reliable and doesn't require network call.

        // Fallback: Generate avatar from user's name or email
        if (!pictureUrl && (user.name || user.email)) {
          const displayText = encodeURIComponent(
            user.name || user.email?.split("@")[0] || "User",
          );
          pictureUrl = `https://ui-avatars.com/api/?name=${displayText}&size=200&background=5260FF&color=fff&bold=true`;
          console.log(`🎨 Using generated avatar for Google OAuth`);
        }
      } else if (provider === "linkedin") {
        // LinkedIn doesn't provide profile pictures in the standard OAuth flow
        // Use generated avatar
        if (user.name || user.email) {
          const displayText = encodeURIComponent(
            user.name || user.email?.split("@")[0] || "User",
          );
          pictureUrl = `https://ui-avatars.com/api/?name=${displayText}&size=200&background=0077B5&color=fff&bold=true`;
          console.log(`🎨 Using generated avatar for LinkedIn`);
        }
      }

      if (!pictureUrl) {
        console.log(`ℹ️ No profile picture URL available for ${provider}`);
        return;
      }

      console.log(`✅ Profile picture URL: ${pictureUrl}`);

      // Upload to Appwrite Storage
      const storageUrl = await this.uploadProfilePictureFromUrl(
        pictureUrl,
        user.$id,
      );

      if (!storageUrl) {
        console.log("⚠️ Failed to upload profile picture to storage");
        return;
      }

      // Store the Appwrite Storage URL in user preferences
      console.log("💾 Saving profile picture URL to preferences...");
      await this.account.updatePrefs(
        mergePrefs("user", user.prefs, { profilePictureUrl: storageUrl }),
      );
      this.invalidateAccountPrefsCache();

      // Update cached user
      if (this.currentUserCache) {
        this.currentUserCache = {
          ...this.currentUserCache,
          photoURL: storageUrl,
        };
        this.notifyAuthStateListeners(this.currentUserCache);
      }

      console.log("✅ OAuth profile picture uploaded and saved successfully");
    } catch (error: any) {
      // Profile picture is optional - don't fail authentication if this fails
      console.error(
        "⚠️ Failed to handle OAuth profile picture (non-critical):",
        error.message,
      );
      console.error("⚠️ Error details:", error);
    }
  }

  async signInWithEmailAndPassword(
    email: string,
    password: string,
  ): Promise<IUser> {
    try {
      // Create email session
      await this.account.createEmailPasswordSession(email, password);

      // Get user details
      const user = await this.account.get();
      const iUser = this.toIUser(user);

      if (!iUser) throw new Error("Sign in failed: No user returned");

      this.currentUserCache = iUser;
      this.notifyAuthStateListeners(iUser);

      return iUser;
    } catch (error: any) {
      console.error("❌ Appwrite sign in error:", error);
      throw new Error(`Sign in failed: ${error.message}`);
    }
  }

  async signInWithGoogle(): Promise<IUser> {
    // Using OAuth2 Token flow (recommended by Appwrite)
    // This avoids third-party cookie issues and works across all browsers
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    // Get production URL from environment or use current origin
    const productionUrl =
      import.meta.env.VITE_APP_URL ||
      "https://brainiacs--os-u12020.vm.elestio.app";

    // Callback URL where OAuth will redirect after authentication
    const baseUrl = isLocalhost ? window.location.origin : productionUrl;
    const successUrl = `${baseUrl}/auth/callback`;
    const failureUrl = `${baseUrl}?auth_error=google`;

    // Get Appwrite endpoint and project ID from environment for diagnostic logging
    const endpoint = import.meta.env.VITE_APPWRITE_ENDPOINT;
    const projectId = import.meta.env.VITE_APPWRITE_PROJECT_ID;

    // The callback URL that Google will redirect to (must be in Google Cloud Console)
    const callbackUrl = `${endpoint}/account/sessions/oauth2/google`;

    console.log(
      "🔐 Initiating Google OAuth with TOKEN flow (third-party cookie safe)...",
    );
    console.log("  App Callback URL:", successUrl);
    console.log("  App Failure URL:", failureUrl);
    console.log("  ");
    console.log(
      "  ⚠️  IMPORTANT: Add this EXACT URL to Google Cloud Console Authorized redirect URIs:",
    );
    console.log("  📋 " + callbackUrl);
    console.log("  ");

    // CRITICAL FIX #1: Clear ALL Appwrite sessions
    try {
      console.log("🧹 Step 1/3: Clearing Appwrite sessions...");
      await this.account.deleteSessions();
      console.log("✅ Appwrite sessions cleared");

      // CRITICAL: Wait to ensure session deletion propagates before OAuth redirect
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log("✅ Session deletion confirmed (waited 500ms)");

      // Verify no sessions remain
      try {
        const remainingSessions = await this.account.listSessions();
        if (remainingSessions.sessions.length > 0) {
          console.error("🚨 WARNING: Sessions still exist after deletion:", remainingSessions.sessions.length);
        } else {
          console.log("✅ Verified: No sessions remaining");
        }
      } catch (verifyError: any) {
        if (verifyError.code === 401) {
          console.log("✅ Verified: No active sessions (401)");
        }
      }
    } catch (error: any) {
      if (error.code !== 401) {
        console.warn("⚠️ Warning: Failed to clear sessions:", error.message);
      } else {
        console.log("ℹ️ No existing sessions (401)");
      }
    }

    // CRITICAL FIX #2: Clear OAuth state
    console.log("🧹 Step 2/3: Clearing OAuth state...");
    localStorage.removeItem("oauth_provider");

    // CRITICAL FIX #3: Force Google to show account picker
    // Add a random state parameter to prevent caching
    const state = `${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
    localStorage.setItem("oauth_state", state);

    // Store provider for callback handling
    localStorage.setItem("oauth_provider", "google");

    // Use session flow so Appwrite completes the session before returning to our app
    console.log("🚀 Initiating OAuth with fresh state...");
    this.account.createOAuth2Session({
      provider: OAuthProvider.Google,
      success: successUrl,
      failure: failureUrl,
    });

    // The browser will redirect before this point is reached
    // Return a Promise that never resolves (page will redirect)
    return new Promise(() => {});
  }

  async signInWithLinkedIn(): Promise<IUser> {
    // Using OAuth2 Token flow (recommended by Appwrite)
    // This avoids third-party cookie issues and works across all browsers
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    // Get production URL from environment or use current origin
    const productionUrl =
      import.meta.env.VITE_APP_URL ||
      "https://brainiacs--os-u12020.vm.elestio.app";

    // Callback URL where OAuth will redirect after authentication
    const baseUrl = isLocalhost ? window.location.origin : productionUrl;
    const successUrl = `${baseUrl}/auth/callback`;
    const failureUrl = `${baseUrl}?auth_error=linkedin`;

    // Get Appwrite endpoint and project ID from environment for diagnostic logging
    const endpoint = import.meta.env.VITE_APPWRITE_ENDPOINT;
    const projectId = import.meta.env.VITE_APPWRITE_PROJECT_ID;

    // The callback URL that LinkedIn will redirect to (must be in LinkedIn Developer Console)
    const callbackUrl = `${endpoint}/account/sessions/oauth2/linkedin`;

    console.log(
      "🔐 Initiating LinkedIn OAuth with TOKEN flow (third-party cookie safe)...",
    );
    console.log("  App Callback URL:", successUrl);
    console.log("  App Failure URL:", failureUrl);
    console.log("  ");
    console.log(
      "  ⚠️  IMPORTANT: Add this EXACT URL to LinkedIn Developer Console Authorized redirect URIs:",
    );
    console.log("  📋 " + callbackUrl);
    console.log("  ");

    // CRITICAL FIX: Clear all existing sessions before initiating OAuth
    // This prevents session reuse causing new users to login as existing users
    try {
      console.log("🧹 Clearing existing sessions before OAuth...");
      await this.account.deleteSessions();
      console.log("✅ All sessions cleared");

      // CRITICAL: Wait to ensure session deletion propagates before OAuth redirect
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log("✅ Session deletion confirmed (waited 500ms)");

      // Verify no sessions remain
      try {
        const remainingSessions = await this.account.listSessions();
        if (remainingSessions.sessions.length > 0) {
          console.error("🚨 WARNING: Sessions still exist after deletion:", remainingSessions.sessions.length);
        } else {
          console.log("✅ Verified: No sessions remaining");
        }
      } catch (verifyError: any) {
        if (verifyError.code === 401) {
          console.log("✅ Verified: No active sessions (401)");
        }
      }
    } catch (error: any) {
      // If no sessions exist (401), that's expected - log and continue
      if (error.code === 401) {
        console.log("ℹ️ No existing sessions to clear (401)");
      } else {
        console.warn("⚠️ Warning: Failed to clear sessions:", error.message);
      }
    }

    // Clear any stale OAuth provider state
    localStorage.removeItem("oauth_provider");

    // Store provider for callback handling (fresh state)
    localStorage.setItem("oauth_provider", "linkedin");

    // Use session flow so Appwrite completes the session before returning to our app
    this.account.createOAuth2Session({
      provider: OAuthProvider.Linkedin,
      success: successUrl,
      failure: failureUrl,
    });

    // The browser will redirect before this point is reached
    // Return a Promise that never resolves (page will redirect)
    return new Promise(() => {});
  }

  /**
   * Handle OAuth callback - completes the OAuth flow by creating a session from the token
   * Call this method when the OAuth provider redirects back to your app
   * @param userId - The user ID from the OAuth callback URL
   * @param secret - The secret token from the OAuth callback URL
   * @param provider - The OAuth provider (google, linkedin) for webhook notification
   */
  async handleOAuthCallback(
    userId: string,
    secret: string,
    provider: "google" | "linkedin",
  ): Promise<IUser> {
    try {
      console.log(`🔐 Processing OAuth callback for ${provider}...`);
      console.log(`   userId: ${userId}`);
      console.log(`   secret: ${secret ? secret.substring(0, 10) + '...' : 'MISSING'}`);

      // Create a session using the OAuth token
      // Note: SDK 20.x uses object parameter syntax
      console.log("🔄 Calling account.createSession...");
      const session = await this.account.createSession({ userId, secret });
      console.log("✅ Session created successfully");

      // Get user details
      console.log("🔄 Calling account.get...");
      const user = await this.account.get();
      console.log("✅ User authenticated:", user.email);

      // CRITICAL DEBUG: Verify userId from callback matches authenticated user
      if (user.$id !== userId) {
        console.error("🚨 CRITICAL: User ID mismatch detected!");
        console.error(`   Expected userId from OAuth: ${userId}`);
        console.error(`   Actual authenticated user: ${user.$id} (${user.email})`);
        console.error("   This indicates a session hijacking or OAuth bug!");
      } else {
        console.log("✅ User ID verification passed");
      }

      // CRITICAL: Verify this is a new user (created recently)
      const userCreatedAt = new Date(user.$createdAt);
      const now = new Date();
      const timeSinceCreation = now.getTime() - userCreatedAt.getTime();
      const isNewUser = timeSinceCreation < 5 * 60 * 1000; // 5 minutes threshold

      console.log("🔍 User creation check:", {
        createdAt: user.$createdAt,
        timeSinceCreation: `${Math.floor(timeSinceCreation / 1000)}s ago`,
        isNewUser
      });

      const iUser = this.toIUser(user);
      this.currentUserCache = iUser;

      // Notify listeners
      this.notifyAuthStateListeners(iUser);

      // Handle OAuth profile picture upload
      await this.handleOAuthProfilePicture(user, provider);

      return iUser;
    } catch (error: any) {
      console.error(`❌ OAuth callback error for ${provider}:`, error);
      console.error(`   Error code: ${error.code}`);
      console.error(`   Error message: ${error.message}`);
      console.error(`   Error type: ${error.type}`);
      throw new Error(`OAuth authentication failed: ${error.message}`);
    }
  }

  async signOut(): Promise<void> {
    try {
      console.log("🚪 Signing out: Clearing all sessions and state...");

      // Delete ALL sessions (not just current) for complete cleanup
      await this.account.deleteSessions();

      // Clear OAuth provider state from localStorage
      localStorage.removeItem("oauth_provider");

      // Clear JWT cache
      this.jwtCache = null;
      this.jwtFailAt = 0;

      this.currentUserCache = null;
      this.notifyAuthStateListeners(null);

      console.log("✅ Sign out complete - all sessions cleared");
    } catch (error: any) {
      console.error("❌ Appwrite sign out error:", error);
      // Still clear cache even if API call fails
      this.currentUserCache = null;
      this.jwtCache = null;
      this.jwtFailAt = 0;
      this.notifyAuthStateListeners(null);
      throw new Error(`Sign out failed: ${error.message}`);
    }
  }

  getCurrentUser(): IUser | null {
    return this.currentUserCache;
  }

  async waitForInitialization(): Promise<void> {
    return this.initializationPromise;
  }

  onAuthStateChanged(callback: (user: IUser | null) => void): () => void {
    // Add listener to the list
    this.authStateListeners.push(callback);

    // Wait for initialization to complete before calling callback for the first time
    // This ensures we don't prematurely return null while checking for active session
    this.initializationPromise.then(() => {
      callback(this.currentUserCache);
    });

    // Return unsubscribe function
    return () => {
      const index = this.authStateListeners.indexOf(callback);
      if (index > -1) {
        this.authStateListeners.splice(index, 1);
      }
    };
  }

  async createUserWithEmailAndPassword(
    email: string,
    password: string,
  ): Promise<IUser> {
    try {
      // Create account
      const userId = "unique()"; // Appwrite auto-generates ID
      await this.account.create(userId, email, password);

      // Sign in to create session
      const iUser = await this.signInWithEmailAndPassword(email, password);

      // Trigger N8N webhook for new user registration
      console.log("📤 Triggering N8N webhook for new user registration...");
      try {
        await n8nService.registerUser({
          user_id: iUser.uid,
          user_email: iUser.email || email,
          user_name: iUser.displayName || undefined,
          auth_provider: "email",
        });
        console.log("✅ N8N user registration webhook sent successfully");
      } catch (webhookError: any) {
        // Don't fail user creation if webhook fails
        console.error(
          "⚠️ N8N webhook failed (non-critical):",
          webhookError.message,
        );
      }

      return iUser;
    } catch (error: any) {
      console.error("❌ Appwrite user creation error:", error);
      throw new Error(`User creation failed: ${error.message}`);
    }
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      const resetUrl = `${window.location.origin}/reset-password`;
      await this.account.createRecovery(email, resetUrl);
    } catch (error: any) {
      console.error("❌ Appwrite password reset error:", error);
      throw new Error(`Password reset failed: ${error.message}`);
    }
  }

  /**
   * Send Magic Link to user's email
   * Uses custom Appwrite Function that sends via Messaging (SendGrid provider)
   */
  async sendMagicLink(email: string): Promise<void> {
    try {
      console.log("📧 Sending Magic Link to:", email);
      const functionUrl = import.meta.env.VITE_APPWRITE_MAGIC_LINK_FUNCTION_URL;

      if (functionUrl) {
        console.log("🔗 Calling magic-link-sender function");

        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Appwrite-Project': this.client.config.project || ''
          },
          body: JSON.stringify({ email })
        });

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'Failed to send magic link');
        }

        console.log("✅ Magic Link sent successfully via Messaging to:", email);
        return;
      }

      const callbackUrl = this.getMagicLinkCallbackUrl();
      console.log("🔗 Magic Link callback configured");

      await this.account.createMagicURLToken(ID.unique(), email, callbackUrl);

      console.log("✅ Magic Link sent successfully via Appwrite SMTP to:", email);
    } catch (error: any) {
      console.error("❌ Magic Link send error:", error);
      throw new Error(`Failed to send magic link: ${error.message}`);
    }
  }

  /**
   * Verify Magic Link token and create session
   * Called after user clicks the magic link in their email
   */
  async verifyMagicLink(userId: string, secret: string): Promise<IUser> {
    try {
      console.log("🔐 Verifying Magic Link for user:", userId);

      // Appwrite (>=1.8) rejects session creation with
      // USER_SESSION_ALREADY_EXISTS when the request already carries an active
      // session (session-fixation protection). A browser that still holds a
      // session cookie from a previous login can therefore never verify a magic
      // link. When that happens, clear the stale session and retry once.
      let session;
      try {
        session = await this.account.updateMagicURLSession(userId, secret);
      } catch (sessionError: any) {
        if (sessionError?.type !== 'user_session_already_exists') {
          throw sessionError;
        }
        // The browser already holds an active session. If it belongs to the same
        // user as the link, keep it — clicking an old/replayed link must not
        // sign them out. Only clear it when switching to a different account.
        let existing: Models.User<Models.Preferences> | null = null;
        try {
          existing = await this.account.get();
        } catch {
          existing = null;
        }
        if (existing && existing.$id === userId) {
          console.log("✅ Magic link clicked while already signed in as the same user — keeping session");
          const iUser = this.toIUser(existing);
          this.currentUserCache = iUser;
          this.notifyAuthStateListeners(iUser);
          return iUser;
        }
        console.log("🧹 Active session for a different account — clearing it before retrying magic link");
        try {
          await this.account.deleteSession('current');
        } catch (clearError: any) {
          if (clearError?.code !== 401) {
            console.warn("⚠️ Could not clear existing session:", clearError?.message);
          }
        }
        session = await this.account.updateMagicURLSession(userId, secret);
      }

      // Get full user details
      const user = await this.account.get();
      const iUser = this.toIUser(user);

      // Update cached user
      this.currentUserCache = iUser;
      this.notifyAuthStateListeners(iUser);

      console.log("✅ Magic Link verification successful:", iUser.email);

      return iUser;
    } catch (error: any) {
      console.error("❌ Appwrite Magic Link verification error:", error);
      throw new Error(`Magic link verification failed: ${error.message}`);
    }
  }

  async acceptTeamInvitation(
    teamId: string,
    membershipId: string,
    userId: string,
    secret: string,
  ): Promise<void> {
    try {
      console.log("👥 Accepting team invitation...", {
        teamId,
        membershipId,
        userId,
      });

      await this.teams.updateMembershipStatus({
        teamId,
        membershipId,
        userId,
        secret,
      });
      this.invalidateTeamsListCache();

      console.log("✅ Team invitation accepted successfully");
    } catch (error: any) {
      console.error("❌ Failed to accept team invitation:", error);
      throw new Error(`Team invitation acceptance failed: ${error.message}`);
    }
  }

  async updateProfile(
    updates: Partial<Pick<IUser, "displayName" | "photoURL">>,
  ): Promise<void> {
    try {
      if (updates.displayName !== undefined) {
        await this.account.updateName(updates.displayName || "");
      }

      // Handle profile picture update
      if (updates.photoURL !== undefined) {
        await this.setProfilePicture(updates.photoURL);
      }

      // Update cached user
      if (this.currentUserCache) {
        this.currentUserCache = {
          ...this.currentUserCache,
          displayName: updates.displayName ?? this.currentUserCache.displayName,
          photoURL: updates.photoURL ?? this.currentUserCache.photoURL,
        };
        this.notifyAuthStateListeners(this.currentUserCache);
      }
    } catch (error: any) {
      console.error("❌ Appwrite profile update error:", error);
      throw new Error(`Profile update failed: ${error.message}`);
    }
  }

  /**
   * Set user profile picture from URL (OAuth provider) or a File/Blob
   * (client-side crop). Uploads to Appwrite Storage, stores the Storage URL
   * (with a cache-busting param) in prefs, and updates the cached user.
   *
   * @param picture - URL string (OAuth), File/Blob (cropped), or null (remove).
   */
  async setProfilePicture(picture: string | File | Blob | null): Promise<void> {
    try {
      const user = await this.account.get();

      if (picture === null) {
        // Remove profile picture
        console.log("🗑️ Removing profile picture...");
        await this.account.updatePrefs(
          mergePrefs("user", user.prefs, { profilePictureUrl: null }),
        );
        this.invalidateAccountPrefsCache();

        // Update cached user
        if (this.currentUserCache) {
          this.currentUserCache = {
            ...this.currentUserCache,
            photoURL: null,
          };
          this.notifyAuthStateListeners(this.currentUserCache);
        }

        console.log("✅ Profile picture removed");
        return;
      }

      // Upload — different path for File/Blob vs URL
      let storageUrl: string | null;
      if (typeof picture === 'string') {
        storageUrl = await this.uploadProfilePictureFromUrl(picture, user.$id);
      } else {
        storageUrl = await this.uploadProfilePictureFromFile(picture, user.$id);
      }

      if (!storageUrl) {
        throw new Error("Failed to upload profile picture to storage");
      }

      // Store the Appwrite Storage URL in user preferences
      console.log("💾 Saving profile picture URL to preferences...");
      await this.account.updatePrefs(
        mergePrefs("user", user.prefs, { profilePictureUrl: storageUrl }),
      );
      this.invalidateAccountPrefsCache();

      // Update cached user
      if (this.currentUserCache) {
        this.currentUserCache = {
          ...this.currentUserCache,
          photoURL: storageUrl,
        };
        this.notifyAuthStateListeners(this.currentUserCache);
      }

      console.log("✅ Profile picture updated successfully");
    } catch (error: any) {
      console.error("❌ Error setting profile picture:", error);
      throw new Error(`Failed to set profile picture: ${error.message}`);
    }
  }

  // JWT token cache to prevent rate limiting
  private jwtCache: { token: string; expiresAt: number } | null = null;
  /** Timestamp of the last failed createJWT — short negative-cache window. */
  private jwtFailAt = 0;

  // Read-through caches for the boot fan-out primitives. At startup several
  // hooks fire in parallel (registration gate, AppLayout team id, prefetch
  // preferences, team prefs, membership roles) and each previously hit the
  // Appwrite REST API directly — 7× /teams + 3× /account/prefs on every boot
  // for data that changes rarely. Every reader below now shares ONE in-flight
  // request per primitive, and resolved values live for a short TTL. Writers
  // invalidate so updates are never served stale beyond the next read.
  // Failures are never cached (asyncRequestCache default), so transient
  // errors re-fire — matches the throw-on-transient-errors pattern.
  private teamsListCache = createAsyncRequestCache<Models.TeamList<Models.Preferences>>({
    ttlMs: 60 * 1000,
  });
  private accountPrefsCache = createAsyncRequestCache<Record<string, any>>({
    ttlMs: 30 * 1000,
  });
  private teamPrefsCache = createAsyncRequestCache<Record<string, any>>({
    ttlMs: 60 * 1000,
  });
  private teamMembershipsCache = createAsyncRequestCache<Models.MembershipList>({
    ttlMs: 60 * 1000,
  });

  async getIdToken(forceRefresh?: boolean): Promise<string | null> {
    try {
      // Check cache first (unless force refresh)
      if (!forceRefresh && this.jwtCache) {
        const now = Date.now();
        // Return cached token if it's not expired (with 5 minute buffer)
        if (this.jwtCache.expiresAt > now + 5 * 60 * 1000) {
          return this.jwtCache.token;
        }
      }

      // Negative cache: if minting just failed (e.g. dead session, rate limit),
      // fail fast instead of stampeding /account/jwts from parallel callers.
      if (Date.now() - this.jwtFailAt < 15_000) {
        return null;
      }

      // Create new JWT token
      const jwt = await this.account.createJWT();

      // Cache the token (Appwrite JWTs expire in 15 minutes by default)
      this.jwtCache = {
        token: jwt.jwt,
        expiresAt: Date.now() + 15 * 60 * 1000, // 15 minutes from now
      };
      this.jwtFailAt = 0;

      return jwt.jwt;
    } catch (error: any) {
      console.error("❌ Appwrite get ID token error:", error);
      this.jwtFailAt = Date.now();
      return null;
    }
  }

  /**
   * Complete user registration with company information
   * Creates Team, adds "client" label, and triggers N8N webhook
   * Implements transaction-like rollback on failure
   */
  async completeRegistration(data: {
    fullName: string;
    companyName: string;
    companyWebsite?: string;
    phoneNumber?: string;
    jobTitle?: string;
    preferredLanguage?: string;
    /** ISO2 country code selected in the registration form (e.g. "ES"). */
    countryCode?: string;
    /** State/province name selected in the registration form (e.g. "Barcelona"). */
    stateName?: string;
  }): Promise<void> {
    let teamId: string | null = null;
    let originalName: string | null = null;

    try {
      const user = await this.account.get();
      originalName = user.name; // Store original name for rollback

      console.log("📋 Completing user registration...");

      // 1. Update user display name
      console.log("  ✏️ Updating display name...");
      await this.account.updateName(data.fullName);

      // 2. Create Team with company name
      console.log("  🏢 Creating company team...");
      const team = await this.teams.create(ID.unique(), data.companyName);
      teamId = team.$id;
      this.invalidateTeamsListCache();
      console.log("  ✅ Team created:", team.$id);

      // 3. Update cached user
      this.currentUserCache = this.toIUser({
        ...user,
        name: data.fullName,
      } as Models.User<Models.Preferences>);
      this.notifyAuthStateListeners(this.currentUserCache);

      // 4. Persist registration info in Appwrite Auth preferences.
      //    SINGLE SOURCE OF TRUTH for the user's language is the `language`
      //    key (matches preferences.service). The legacy `preferred_language`
      //    key is NOT written — N8N may still read it from the webhook payload.
      //    Geo values resolve through geoService (Appwrite geo collections):
      //    country = FULL NAME, language = the country's primary BCP 47 tag.
      console.log("  💾 Saving registration info to Auth preferences...");
      const resolvedCountryName = data.countryCode
        ? await geoService.countryName(data.countryCode)
        : null;
      await this.account.updatePrefs(
        mergePrefs("user", user.prefs, {
          full_name: data.fullName,
          job_title: data.jobTitle || null,
          language: data.preferredLanguage
            ? normalizeLocale(data.preferredLanguage)
            : await geoService.primaryLocale(data.countryCode || ""),
          // Mirror the geo selections so user prefs stay in sync with the team.
          country: resolvedCountryName,
          country_code: data.countryCode || null,
          state: data.stateName || null,
          status: "active",
          registration_completed_at: new Date().toISOString(),
        }),
      );
      this.invalidateAccountPrefsCache();

      // 5. Seed the full Team preference key set (modules, localization,
      //    billing, external-account placeholders). Timezone comes from the
      //    selected state when available (geo_states.timezone), falling back
      //    to the country's primary zone; currency from the country.
      console.log("  👥 Seeding default Team preferences...");
      const resolvedTimezone = data.countryCode
        ? await geoService.timezoneForState(data.countryCode, data.stateName || '')
        : null;
      const resolvedCurrency = data.countryCode
        ? await geoService.currencyForCountry(data.countryCode)
        : null;
      const defaultTeamPrefs = buildDefaultTeamPrefs({
        companyWebsite: data.companyWebsite,
        ownerEmail: user.email,
        language: data.preferredLanguage
          ? normalizeLocale(data.preferredLanguage)
          : (await geoService.primaryLocale(data.countryCode || "")) || "en-GB",
        countryCode: data.countryCode,
        countryName: resolvedCountryName,
        stateName: data.stateName,
        timezone: resolvedTimezone,
        currency: resolvedCurrency,
      });
      await this.teams.updatePrefs(team.$id, defaultTeamPrefs);

      // 6. Detect actual auth provider from session
      console.log("  🔍 Detecting auth provider...");
      const sessions = await this.account.listSessions();
      const currentSession = sessions.sessions[0]; // Most recent session
      const sessionProvider = currentSession?.provider || "email";
      // Cast to proper type (Appwrite returns string, but we know it's one of these)
      const actualProvider = (sessionProvider === "google" || sessionProvider === "linkedin")
        ? sessionProvider
        : "email" as "google" | "linkedin" | "email";
      console.log(`  ✅ Auth provider detected: ${actualProvider}`);

      // 7. Trigger N8N webhook with complete registration data
      console.log("  📤 Sending registration data to N8N...");
      try {
        await n8nService.registerUser({
          user_id: user.$id,
          user_email: user.email,
          user_name: data.fullName,
          company_name: data.companyName,
          company_website: data.companyWebsite,
          phone_number: data.phoneNumber,
          job_title: data.jobTitle,
          preferred_language: data.preferredLanguage,
          team_id: team.$id,
          country_code: data.countryCode,
          state_name: data.stateName,
          auth_provider: actualProvider, // FIXED: Use actual provider from session
        });
        console.log("  ✅ N8N webhook sent successfully");
      } catch (webhookError: any) {
        // Don't fail registration if webhook fails - user is already registered
        console.error(
          "  ⚠️ N8N webhook failed (non-critical):",
          webhookError.message,
        );
        console.error(
          "  ⚠️ User registration completed but N8N was not notified",
        );
      }

      console.log("✅ Registration completed successfully!");
    } catch (error: any) {
      console.error("❌ Registration completion error:", error);

      // ROLLBACK: Attempt to undo changes if registration failed
      console.log("🔄 Attempting to rollback registration changes...");

      try {
        // Rollback user name if it was changed
        if (originalName !== null) {
          console.log("  ↩️  Restoring original user name...");
          await this.account.updateName(originalName);
        }

        // Rollback team creation if team was created
        if (teamId) {
          console.log("  🗑️  Deleting created team...");
          await this.teams.delete(teamId);
          this.invalidateTeamsListCache();
        }

        console.log("✅ Rollback completed successfully");
      } catch (rollbackError: any) {
        console.error(
          "⚠️ Rollback failed (non-critical):",
          rollbackError.message,
        );
        console.error(
          "⚠️ Some registration artifacts may remain - please contact support",
        );
      }

      throw new Error(`Failed to complete registration: ${error.message}`);
    }
  }

  /**
   * Check if user has completed registration
   * Returns true if user has the "client" label
   */
  async hasCompletedRegistration(): Promise<boolean> {
    // A user has completed registration if they are a member of at least one team.
    // CRITICAL: "couldn't check" must NEVER be reported as "not registered".
    // Returning false on transient errors makes TanStack cache a successful
    // `false`, which flips isRegistered off and redirects a registered user to
    // /register (the "onboarding view hijack" bug). Transient failures THROW so
    // the query enters error state and the last successful value stays cached.
    const teamList = await this.getTeamsList();
    const hasTeam = teamList.total > 0;

    console.log("🔍 Registration status check (by team membership):", {
      userId: this.currentUserCache?.uid,
      numberOfTeams: teamList.total,
      hasTeam: hasTeam,
    });

    return hasTeam;
  }

  /**
   * Shared /teams list read. All team helpers go through this instead of
   * calling this.teams.list() raw — concurrent boot callers share ONE request
   * and the result lives in teamsListCache for 60s.
   */
  private getTeamsList(): Promise<Models.TeamList<Models.Preferences>> {
    return this.teamsListCache.get('list', () => this.teams.list<Models.Preferences>());
  }

  async getTeamName(): Promise<string | null> {
    try {
      const teamList = await this.getTeamsList();
      return teamList.teams[0]?.name ?? null;
    } catch {
      return null;
    }
  }

  async getTeamId(): Promise<string | null> {
    try {
      const teamList = await this.getTeamsList();
      return teamList.teams[0]?.$id ?? null;
    } catch {
      return null;
    }
  }

  async getTeamMembershipRoles(): Promise<string[]> {
    try {
      const userId = this.currentUserCache?.uid;
      if (!userId) return [];

      const teamList = await this.getTeamsList();
      const teamId = teamList.teams[0]?.$id;
      if (!teamId) return [];

      // Fetch ALL memberships for the team (the Client SDK doesn't reliably
      // support query filters on listMemberships), then find the caller.
      const memberships = await this.getTeamMembershipsById(teamId);
      const currentMembership = memberships.memberships.find(
        (m) => m.userId === userId && m.confirm,
      );
      return currentMembership?.roles || [];
    } catch (error) {
      console.error('Error getting current Appwrite Team membership roles:', error);
      return [];
    }
  }

  async getTeamPreferences(): Promise<Record<string, any>> {
    try {
      const teamList = await this.getTeamsList();
      const team = teamList.teams[0];
      const teamId = team?.$id;

      if (!teamId) {
        return {};
      }

      const prefs = await this.getTeamPrefsById(teamId);
      return {
        ...decodeStoragePrefs(prefs as Record<string, any>),
        team_name: team.name,
      };
    } catch (error: any) {
      console.error("Error getting team preferences:", error);
      return {};
    }
  }

  async updateTeamPreferences(updates: Record<string, any>): Promise<boolean> {
    try {
      const teamList = await this.getTeamsList();
      const teamId = teamList.teams[0]?.$id;

      if (!teamId) {
        console.warn("Cannot update team preferences: user has no team");
        return false;
      }

      // Merge with existing prefs + template defaults so we never clobber
      // keys we didn't send (billing_email, lvl1_id, module flags, …).
      const existing = (await this.getTeamPrefsById(teamId)) as Record<string, any>;
      await this.teams.updatePrefs(teamId, mergePrefs("team", existing, updates));
      this.invalidateTeamsListCache();
      return true;
    } catch (error: any) {
      console.error("Error updating team preferences:", error);
      return false;
    }
  }

  /**
   * Shared /account/prefs read — concurrent boot callers (preferences.user +
   * profile.client) share ONE request; value lives in accountPrefsCache 30s.
   */
  private getAccountPrefs(): Promise<Models.Preferences> {
    return this.accountPrefsCache.get('prefs', () => this.account.getPrefs());
  }

  /** Drop the prefs cache after any write so reads see the new values. */
  private invalidateAccountPrefsCache(): void {
    this.accountPrefsCache.clear();
  }

  /** Drop the teams cache after membership/team changes. */
  private invalidateTeamsListCache(): void {
    this.teamsListCache.clear();
    this.teamPrefsCache.clear();
    this.teamMembershipsCache.clear();
  }

  /** Shared teams/<id>/prefs read — one request per TTL window. */
  private getTeamPrefsById(teamId: string): Promise<Record<string, any>> {
    return this.teamPrefsCache.get(teamId, () =>
      this.teams.getPrefs<Record<string, any>>(teamId),
    );
  }

  /** Shared teams/<id>/memberships read — one request per TTL window. */
  private getTeamMembershipsById(teamId: string): Promise<Models.MembershipList> {
    return this.teamMembershipsCache.get(teamId, () =>
      this.teams.listMemberships(teamId) as Promise<Models.MembershipList>,
    );
  }

  /**
   * Get user preferences
   * Returns an object with user preferences (language, theme, timezone, etc.)
   * Storage "null" strings are decoded to real nulls for app-side logic.
   */
  async getPreferences(): Promise<Record<string, any>> {
    try {
      const prefs = await this.getAccountPrefs();
      return decodeStoragePrefs(prefs as Record<string, any>);
    } catch (error: any) {
      console.error("❌ Error getting user preferences:", error);
      return {};
    }
  }

  /**
   * Update user preferences.
   *
   * Appwrite's account.updatePrefs() REPLACES the whole prefs object — sending
   * only changed keys would wipe every other key (profilePictureUrl,
   * client_id, …). This is the merge point: read fresh prefs, overlay the
   * template defaults so no key is ever absent, apply the updates, write once.
   */
  async updatePreferences(updates: Record<string, any>): Promise<void> {
    try {
      const existing = await this.getAccountPrefs();
      await this.account.updatePrefs(mergePrefs("user", existing, updates));
      this.invalidateAccountPrefsCache();
    } catch (error: any) {
      console.error("❌ Error updating user preferences:", error);
      throw new Error(`Failed to update preferences: ${error.message}`);
    }
  }

  /**
   * Set target email for admin impersonation
   */
  setTargetEmail(email: string | null): void {
    // Basic security: Only superadmin users can impersonate others
    const isSuperAdmin =
      this.currentUserCache?.labels?.includes('superadmin') ||
      this.currentUserCache?.email === 'brainiac@brainiacsbots.com';
    if (!isSuperAdmin && email !== null) {
      console.warn('🚨 [IMPERSONATION] Security Attempt blocked: Non-admin tried to set target email.');
      return;
    }

    this.targetEmail = email;

    if (email) {
      sessionStorage.setItem(this.TARGET_EMAIL_STORAGE_KEY, email);
      console.log('👤 [IMPERSONATION] Active:', email);
    } else {
      sessionStorage.removeItem(this.TARGET_EMAIL_STORAGE_KEY);
      console.log('👤 [IMPERSONATION] Cleared');
    }

    // Notify auth state listeners so components refresh
    this.notifyAuthStateListeners(this.currentUserCache);
  }

  /**
   * Get target email for admin impersonation
   */
  getTargetEmail(): string | null {
    // Only return target email if current user is superadmin
    const isSuperAdmin =
      this.currentUserCache?.labels?.includes('superadmin') ||
      this.currentUserCache?.email === 'brainiac@brainiacsbots.com';
    if (!isSuperAdmin) return null;
    return this.targetEmail;
  }
}
